<template>
  <SettingComponentPage :title="title" :summary="summary">
    <CCard class="invoice-issuer-edit">
      <CCardBody>
        <!-- 上端に出すのは読み込みの失敗だけ。保存の断りは［保存する］のすぐ上に出す。 -->
        <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>

        <div v-if="loading" class="invoice-issuer-edit__loading">読み込んでいます…</div>

        <!--
          **読み込めたときだけ入力欄を出す。**
          空のフォームを出すと、その空で保存できて、入っていた住所や振込先が消える。
        -->
        <template v-else-if="loaded">
          <!--
            会社名と電話番号も請求書に載るが、ここでは直さない
            （自社設定の「会社情報」が持つ。同じ値を2か所から書かない）。
            載る値は見せておく。見えないと、請求書を開くまで何が載るか分からない。
          -->
          <dl class="invoice-issuer-edit__fixed">
            <div><dt>会社名</dt><dd>{{ display(received.company_name) }}</dd></div>
            <div><dt>電話番号</dt><dd>{{ display(received.telephone) }}</dd></div>
          </dl>
          <p class="invoice-issuer-edit__note">{{ fixedNote }}</p>

          <div class="setting-fields">
            <template v-for="field in fields">
              <label :key="field.key + '-label'" :for="'invoice-issuer-' + field.key">{{ field.label }}</label>
              <CInput
                :id="'invoice-issuer-' + field.key"
                :key="field.key + '-input'"
                v-model.trim="form[field.key]"
                :maxlength="field.maxLength"
                :placeholder="field.placeholder"
                :disabled="saving"
                :description="field.maxLength + '文字まで。空のままでも保存できます。'"
              />
            </template>

            <label for="invoice-issuer-seal">{{ sealLabel }}</label>
            <div>
              <!--
                **消すつもりの表示を、いちばん先に出す。**チェックを入れたのに
                「登録済み」のままだと、保存で何が起きるか読めない。
              -->
              <span v-if="removeSeal">保存すると消えます</span>
              <span v-else-if="sealImage">選択しました</span>
              <span v-else-if="received.has_seal">登録済み</span>
              <span v-else>未登録</span>
              <input
                id="invoice-issuer-seal"
                ref="sealInput"
                type="file"
                accept="image/png,image/jpeg"
                :disabled="saving || removeSeal"
                class="ml-2"
                @change="pickSeal"
              >
              <label v-if="received.has_seal" class="invoice-issuer-edit__seal-remove">
                <input
                  type="checkbox"
                  v-model="removeSeal"
                  :disabled="saving"
                  @change="onRemoveSealChanged"
                >
                角印を消す
              </label>
              <div class="small text-muted">
                PNG か JPEG、1MBまで。
                <!--
                  **下の2文は、角印が登録済みのときだけ出す**（上の「角印を消す」の
                  チェックと同じ条件）。未登録のときは「いまの角印」が無く、チェックも
                  画面に無いので、どちらの文も当てはまらない（2026-10-09、画面の確かめで
                  見つかった）。未登録のときに残る注意は、上の画像の形式だけ。
                -->
                <template v-if="received.has_seal">
                  <span class="invoice-issuer-edit__seal-keep-note">選ばなければ、いまの角印をそのまま残します。</span>
                  <span class="invoice-issuer-edit__seal-remove-note">消すときは「角印を消す」にチェックを入れて保存してください。</span>
                </template>
              </div>
            </div>
          </div>

          <ActionResultMessage :text="saveError" type="danger"/>
          <div class="invoice-issuer-edit__actions">
            <CButton
              type="button"
              color="secondary"
              variant="outline"
              :disabled="saving"
              @click="back"
            >やめる</CButton>
            <!--
              **角印の画像を読み込んでいる間は押せない。**読み込みが終わる前に保存すると、
              選んだ画像を送らずに保存が通り、選んだのに元の角印のまま残る。
            -->
            <CButton
              type="button"
              color="primary"
              :disabled="saving || sealReading"
              @click="save"
            >{{ saving ? '保存しています…' : '保存する' }}</CButton>
          </div>
        </template>
      </CCardBody>
    </CCard>
  </SettingComponentPage>
</template>

<script>
import api from '@/services/companySettings'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import SettingComponentPage from './SettingComponentPage.vue'
import {
  INVOICE_ISSUER_TEXT_FIELDS,
  INVOICE_ISSUER_SEAL_LABEL,
  invoiceIssuerFormFrom
} from '../invoiceIssuerFields'
import {
  INVOICE_ISSUER_TITLE,
  INVOICE_ISSUER_SUMMARY,
  INVOICE_ISSUER_FIXED_NOTE
} from '../invoiceIssuerTexts'

/**
 * 請求書記載情報（住所・建物名・インボイス登録番号・請求担当者・振込先・
 * 角印）を直す画面。
 *
 * <h2>どこから来るか</h2>
 *
 * 自社設定の一覧の「請求書記載情報」のカードの［変更］。
 * 請求書管理で、まだ空の項目があるときの案内からも、この画面へ案内する。
 *
 * <h2>ほかの編集画面と口が違う</h2>
 *
 * 自社設定のほかの編集画面は、全項目をまとめて送る口（/setting/update）で保存する。
 * ここは専用の口で、文字の5項目を全部そろえて送る（1つでも欠けるとサーバが断る）。
 * 角印は、選んだときだけ送る。選ばなければ鍵ごと送らない＝今のまま。
 * 「角印を消す」のときは空文字で送る。
 */
export default {
  name: 'InvoiceIssuerEdit',
  components: { SettingComponentPage, ActionResultMessage },

  data () {
    return {
      title: INVOICE_ISSUER_TITLE,
      summary: INVOICE_ISSUER_SUMMARY,
      fixedNote: INVOICE_ISSUER_FIXED_NOTE,
      fields: INVOICE_ISSUER_TEXT_FIELDS,
      sealLabel: INVOICE_ISSUER_SEAL_LABEL,
      /** サーバから読んだ値（会社名・電話番号・角印の有無もここ）。 */
      received: {},
      /** 入力欄の値。文字の5項目。 */
      form: invoiceIssuerFormFrom(null),
      /** 選んだ角印（base64）。**選ばなければ null のまま**＝送らない。 */
      sealImage: null,
      /**
       * 角印の画像を読み込んでいる最中か。**この間は保存しない。**
       * 読み込みの前に保存すると sealImage がまだ null で、角印を送らずに保存が通る。
       */
      sealReading: false,
      /**
       * 角印を選んだ（選び直した・取り消した・消すことにした）回数。
       * 読み込みは選んだ順に終わるとは限らない。終わったときにこの番号が自分の番と
       * 違えば、あとから別の操作があったので、その結果は捨てる。
       */
      sealPick: 0,
      /** 角印を消すか。消すのと差し替えるのは両立させない。 */
      removeSeal: false,
      loading: true,
      loaded: false,
      saving: false,
      errorMessage: '',
      saveError: ''
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.errorMessage = ''
      try {
        const received = await api.getInvoiceIssuer()
        this.received = received && typeof received === 'object' ? received : {}
        this.form = invoiceIssuerFormFrom(this.received)
        this.loaded = true
      } catch (error) {
        this.errorMessage = '自社設定を読み込めませんでした。' +
          ' 時間をおいて開き直してください。'
      } finally {
        this.loading = false
      }
    },

    /** 角印を選んだ。base64 にして持つ。送るのは保存のときだけ。 */
    pickSeal (event) {
      /*
       * **選ぶたびに番号を進める。**前に選んだ画像の読み込みがまだ終わっていなくても、
       * その結果は下の番号の照合で捨てる（最後に選んだ画像だけが残る）。
       */
      const pick = ++this.sealPick
      const file = event.target.files && event.target.files[0]
      if (!file) {
        this.sealReading = false
        this.sealImage = null
        return
      }
      const reader = new FileReader()
      this.sealReading = true
      reader.onload = () => {
        if (pick !== this.sealPick) return
        /* data:image/png;base64,XXXX の後ろだけ取る。 */
        const text = String(reader.result || '')
        const at = text.indexOf(',')
        this.sealImage = at >= 0 ? text.slice(at + 1) : null
        this.sealReading = false
      }
      reader.onerror = () => {
        if (pick !== this.sealPick) return
        this.saveError = '角印の画像を読み取れませんでした。'
        this.sealImage = null
        this.sealReading = false
      }
      reader.readAsDataURL(file)
    },

    /** 「角印を消す」を切り替えた。消す側を選んだら、選んであった画像は捨てる。 */
    onRemoveSealChanged () {
      if (!this.removeSeal) return
      /* 読み込みの途中の画像も捨てる（あとから読み込みが終わって、選んだことにならないように）。 */
      this.sealPick++
      this.sealReading = false
      this.sealImage = null
      if (this.$refs.sealInput) {
        /* 選んだファイル名が残ると、消すのに「選択しました」に見える。 */
        this.$refs.sealInput.value = ''
      }
    },

    /**
     * 送る本文。**文字の5項目は必ず全部入れる。**
     * 角印は、消すなら空文字・選んだなら base64・どちらでもなければ鍵ごと入れない。
     */
    buildPayload () {
      const payload = {}
      this.fields.forEach(field => {
        const value = this.form[field.key]
        payload[field.key] = value === null || value === undefined ? '' : String(value).trim()
      })
      if (this.removeSeal) {
        payload.seal_image = ''
      } else if (this.sealImage) {
        payload.seal_image = this.sealImage
      }
      return payload
    },

    async save () {
      if (this.saving) return
      /* 角印の読み込みが終わる前には送らない（選んだ画像が入らないまま保存が通る）。 */
      if (this.sealReading) return
      this.saving = true
      this.saveError = ''
      try {
        await api.saveInvoiceIssuer(this.buildPayload())
        this.$emit('saved')
        this.$router.push({ name: '設定' }).catch(() => {})
      } catch (error) {
        this.saveError = this.refusalMessage(error)
      } finally {
        this.saving = false
      }
    },

    /** サーバが断った理由を、そのまま出す（長すぎる項目の名前と上限が入っている）。 */
    refusalMessage (error) {
      const body = error && error.response && error.response.data
      const message = body && typeof body.message === 'string' ? body.message.trim() : ''
      return message || '保存できませんでした。入力内容を確認してください。'
    },

    display (value) {
      return value == null || String(value).trim() === '' ? '未設定' : value
    },

    back () {
      this.$router.push({ name: '設定' }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.invoice-issuer-edit__loading {
  color: #5c6873;
}

.invoice-issuer-edit__fixed {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.5rem 1.5rem;
  max-width: 34rem;
  margin: 0;
}

.invoice-issuer-edit__fixed dt {
  font-size: 0.8rem;
  font-weight: 600;
  color: #5c6873;
}

.invoice-issuer-edit__fixed dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.invoice-issuer-edit__note {
  margin: 0.5rem 0 0;
  max-width: 34rem;
  font-size: 0.8rem;
  color: #5c6873;
  line-height: 1.6;
}

.setting-fields {
  display: grid;
  gap: 0.25rem;
  max-width: 34rem;
}

.setting-fields label {
  margin-top: 0.75rem;
  font-weight: 600;
}

.invoice-issuer-edit__seal-remove {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0 0 0 0.75rem;
  font-weight: 400;
}

.invoice-issuer-edit__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}
</style>
